'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  KiaoModal,
  ReceiptRows,
  SettingRow,
  Stepper,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import type { TeamSubscriptionSummary, TrialSeatQuote } from '@kiao-voice/api-contract/organization'
import { writeBillingThreeDsIntent } from '../billing/three-ds-intent'
import {
  cancelTrialContinuation,
  purchaseTrialSeats,
  quoteTrialSeats,
  resumeTrialContinuation,
  seatPaymentRefusalMessageKey,
} from './api-client'
import { formatTwd, MAX_TEAM_SEATS, toRenewalDate } from './seat-adjustment'
import { resolveNextPeriodLine, type TeamBillingMode } from './team-billing-state'

type TrialMode = Extract<TeamBillingMode, { kind: 'trial' }>

export interface TrialSeatSectionProps {
  subscription: TeamSubscriptionSummary
  mode: TrialMode
  /** 使用中＋邀請中的席位（`seatUsage.used`）。 */
  used: number
  /** 3DS 轉跳 seam（測試注入）。 */
  redirect?: (url: string) => void
  onDone: () => void
  onError: (cause: unknown) => void
}

/**
 * `01 席次` 的試用版：贈送席次與到期日、已購席次、試用結束後的續訂摘要，
 * 以及購買／調整購買數、取消／恢復自動續訂。可不可以操作來自伺服器的
 * `capabilities`（經 `resolveTeamBillingMode`），本期金額只顯示報價端點的數字。
 */
export function TrialSeatSection({
  subscription,
  mode,
  used,
  redirect,
  onDone,
  onError,
}: TrialSeatSectionProps) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const total = subscription.seats ?? 0
  const trialEndDate = toRenewalDate(subscription.complimentaryEndsAt ?? null)
  const giftEndDate = toRenewalDate(mode.giftEndsAt)
  const purchaseBlockedKey = seatPaymentRefusalMessageKey(mode.purchaseBlockedReason)
  const nextLine = resolveNextPeriodLine(subscription)
  const renewalSeats = subscription.nextPeriod?.seats ?? mode.purchasedSeats

  const changeContinuation = useCallback(
    async (action: 'cancel' | 'resume') => {
      const params = { date: trialEndDate ?? '', seats: renewalSeats }
      const ok = await confirm(
        action === 'cancel'
          ? {
              title: t('team.trialCancelConfirmTitle'),
              message: t('team.trialCancelConfirmBody', params),
              confirmText: t('team.trialCancelConfirmCta'),
              cancelText: t('common:cancel'),
            }
          : {
              title: t('team.trialResumeConfirmTitle'),
              message: t('team.trialResumeConfirmBody', params),
              confirmText: t('team.trialResumeConfirmCta'),
              cancelText: t('common:cancel'),
            }
      )
      if (!ok) return
      setBusy(true)
      try {
        if (action === 'cancel') await cancelTrialContinuation()
        else await resumeTrialContinuation()
        toast.success(t(action === 'cancel' ? 'team.trialCanceledToast' : 'team.trialResumedToast'))
        onDone()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/trial-seat-section.tsx' })
        onError(cause)
      } finally {
        setBusy(false)
      }
    },
    [confirm, onDone, onError, renewalSeats, t, trialEndDate]
  )

  const purchase = useCallback(
    async (purchasedSeats: number) => {
      setBusy(true)
      try {
        const result = await purchaseTrialSeats(purchasedSeats)
        if (result.requires3ds && result.paymentUrl) {
          // 試用加購與付費加席是同一種交易，銀行驗證回來走 team_seat_addon 的
          // 回程；API 的回程網址已帶這組對應，這裡只是同分頁的備援。
          if (result.tradeId) {
            writeBillingThreeDsIntent({ product: 'team_seat_addon', tradeId: result.tradeId })
          }
          if (redirect) redirect(result.paymentUrl)
          else if (typeof window !== 'undefined') window.location.href = result.paymentUrl
          return
        }
        setPurchaseOpen(false)
        toast.success(
          typeof result.chargedTwd === 'number'
            ? t('team.trialPurchasedToast', {
                seats: purchasedSeats,
                amount: formatTwd(result.chargedTwd),
              })
            : t('team.trialPurchasedToastNoCharge', { seats: purchasedSeats })
        )
        onDone()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/trial-seat-section.tsx' })
        setPurchaseOpen(false)
        onError(cause)
      } finally {
        setBusy(false)
      }
    },
    [onDone, onError, redirect, t]
  )

  return (
    <div data-testid="trial-seat-section">
      <SettingRow
        label={t('team.trialHeading')}
        description={
          <>
            {giftEndDate
              ? t('team.trialGiftSeats', { seats: mode.giftSeats, date: giftEndDate })
              : t('team.trialGiftSeatsNoDate', { seats: mode.giftSeats })}
            <span className="block">
              {t('team.trialPurchasedSeats', { seats: mode.purchasedSeats })}
            </span>
          </>
        }
        control={
          <span className="flex items-baseline gap-[var(--gap-xs)]">
            <span
              className="font-display text-2xl tabular-nums leading-none"
              aria-label={t('team.seatUsageAria', { used, total })}
            >
              <span data-testid="trial-seat-used">{used}</span>
              <span className="px-0.5 text-muted">/</span>
              <span data-testid="trial-seat-total">{total}</span>
            </span>
            <span className="text-xs text-muted">{t('team.seatUnit')}</span>
          </span>
        }
      />

      <SettingRow
        label={t('team.trialAfterLabel')}
        description={
          <span data-testid="trial-next-period">
            {nextLine ? t(nextLine.key, nextLine.params) : t('team.trialNoPurchaseLine')}
          </span>
        }
        control={
          mode.canCancel ? (
            <Button
              variant="secondary"
              size="control"
              disabled={busy}
              onClick={() => void changeContinuation('cancel')}
            >
              {t('team.trialCancelContinuationCta')}
            </Button>
          ) : mode.canResume ? (
            <Button
              variant="secondary"
              size="control"
              disabled={busy}
              onClick={() => void changeContinuation('resume')}
            >
              {t('team.trialResumeContinuationCta')}
            </Button>
          ) : undefined
        }
      />

      <SettingRow
        label={t('team.trialPurchaseLabel')}
        description={t('team.trialPurchaseDesc')}
        control={
          <Button
            variant={mode.purchasedSeats > 0 ? 'secondary' : 'primary'}
            size="control"
            disabled={busy || !mode.canPurchase}
            onClick={() => setPurchaseOpen(true)}
          >
            {t(mode.purchasedSeats > 0 ? 'team.trialAdjustCta' : 'team.trialPurchaseCta')}
          </Button>
        }
        supportingContent={
          purchaseBlockedKey ? (
            <Alert tone="warn" data-testid="trial-purchase-blocked">
              {t(purchaseBlockedKey)}
            </Alert>
          ) : undefined
        }
      />

      {purchaseOpen ? (
        <TrialPurchaseModal
          initialSeats={Math.max(1, mode.purchasedSeats)}
          cycle={subscription.cycle}
          busy={busy}
          onClose={() => setPurchaseOpen(false)}
          onConfirm={(seats) => void purchase(seats)}
        />
      ) : null}
    </div>
  )
}

type QuoteState =
  | { status: 'loading' }
  | { status: 'ready'; quote: TrialSeatQuote }
  | { status: 'error' }

/**
 * 購買確認：每個購買數都向伺服器報價，顯示本期實收金額、效期（到試用結束）與
 * 試用結束後的自動續訂。報價失敗或還沒回來時不能確認——那等於授權一筆沒報價的扣款。
 */
function TrialPurchaseModal({
  initialSeats,
  cycle,
  busy,
  onClose,
  onConfirm,
}: {
  initialSeats: number
  cycle: TeamSubscriptionSummary['cycle']
  busy: boolean
  onClose: () => void
  onConfirm: (seats: number) => void
}) {
  const { t } = useTranslation('web')
  const [seats, setSeats] = useState(initialSeats)
  const [attempt, setAttempt] = useState(0)
  const [quote, setQuote] = useState<QuoteState>({ status: 'loading' })

  useEffect(() => {
    // 只有最後一次選的購買數能寫入：較慢回來的舊報價不能蓋掉新的。
    let current = true
    setQuote({ status: 'loading' })
    quoteTrialSeats(seats)
      .then((next) => {
        if (current) setQuote({ status: 'ready', quote: next })
      })
      .catch((cause) => {
        reportHandledException(cause, { source: 'features/team/trial-seat-section.tsx' })
        if (current) setQuote({ status: 'error' })
      })
    return () => {
      current = false
    }
  }, [seats, attempt])

  const ready =
    quote.status === 'ready' && quote.quote.purchasedSeats === seats ? quote.quote : null
  const endDate = ready ? (toRenewalDate(ready.trialEndsAt) ?? ready.trialEndsAt) : null

  return (
    <KiaoModal
      open
      onClose={onClose}
      title={t('team.trialPurchaseTitle')}
      subtitle={t('team.trialPurchaseSubtitle')}
      closeLabel={t('common:dismiss')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common:cancel')}
          </Button>
          <Button variant="primary" onClick={() => onConfirm(seats)} disabled={busy || !ready}>
            {t('team.trialPurchaseConfirmCta')}
          </Button>
        </>
      }
    >
      <div className="mb-[var(--gap-md)] flex items-center justify-between gap-[var(--gap-sm)]">
        <span className="text-sm">{t('team.trialPurchaseInputLabel')}</span>
        <Stepper
          editable
          inputLabel={t('team.trialPurchaseInputLabel')}
          value={seats}
          min={1}
          max={MAX_TEAM_SEATS}
          decrementLabel={t('team.removeSeatAria')}
          incrementLabel={t('team.addSeatAria')}
          onChange={setSeats}
        />
      </div>

      {quote.status === 'error' ? (
        <div className="flex flex-col items-start gap-[var(--gap-sm)]">
          <Alert tone="danger" data-testid="trial-quote-error">
            {t('team.trialQuoteError')}
          </Alert>
          <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>
            {t('team.trialQuoteRetry')}
          </Button>
        </div>
      ) : ready && endDate ? (
        <ReceiptRows
          data-testid="trial-quote-summary"
          rows={[
            {
              key: 'renewal',
              label: t('team.seatProrateRenewalLabel'),
              value: t(
                cycle === 'yearly' ? 'team.trialQuoteRenewalYearly' : 'team.trialQuoteRenewal',
                {
                  seats,
                  date: endDate,
                  amount: formatTwd(ready.perSeatPriceTwd * seats),
                }
              ),
              'data-testid': 'trial-quote-renewal',
            },
            {
              key: 'charge',
              label: (
                <>
                  {t('team.trialQuoteLine')}
                  {ready.addedSeats > 0 ? (
                    <span className="mt-0.5 block text-xs" data-testid="trial-quote-until">
                      {t('team.trialQuoteUntil', { seats: ready.addedSeats, date: endDate })}
                    </span>
                  ) : null}
                </>
              ),
              value:
                ready.amountTwd > 0
                  ? t('team.seatProrateAmount', { amount: formatTwd(ready.amountTwd) })
                  : t('team.trialQuoteNoCharge'),
              variant: 'total' as const,
              'data-testid': 'trial-quote-charge',
            },
          ]}
        />
      ) : (
        <p className="text-sm text-muted" data-testid="trial-quote-loading">
          {t('team.trialQuoteLoading')}
        </p>
      )}
    </KiaoModal>
  )
}
